import qs from 'qs'
import { API } from '@/shared/plugins/Api/API'
import { exportBaseParams, exportRequestOptions, exportResult, readBlobError } from '@/shared/plugins/Api/EventExportHelpers'

class AuditTrails extends API {
  constructor (options) {
    super('FPAPI', true)
  }

  /**
   * Fetches a paginated list of audit trail events for a given organization.
   *
   * Cursor-based pagination:
   *   - The API returns a `x-pagination-cursor-next` response header when more pages exist.
   *   - Pass that value back as `params.cursor` to fetch the next page.
   *   - When `nextCursor` in the returned object is `null`, all pages have been consumed.
   *
   * @param {string} organizationId - Organization identifier sent via the `Organization-Id` header.
   * @param {Object} params
   * @param {number}  [params.pageSize=100] - Number of events per page.
   * @param {string}  [params.cursor]       - Cursor returned from a previous call to fetch the next page.
   * @param {Array}   [params.resources]    - Filter by resource types (serialized as comma-separated values).
   * @param {string}  [params.search]       - Free-text query, sent as `q`. Matches action, service, resource type, user agent and the change payloads.
   * @param {string|Date} [params.from]     - Start of the time range (converted to RFC 3339).
   * @param {string|Date} [params.to]       - End of the time range (converted to RFC 3339).
   * @returns {Promise<{data: Array, nextCursor: string|null}>}
   */
  async list (organizationId, params = {}) {
    const headers = {
      'Organization-Id': organizationId,
      'X-Pagination-Size': params.pageSize || 120
    }
    if (params.cursor) {
      headers['X-Pagination-Cursor'] = params.cursor
    }

    const response = await this.request({
      method: 'get',
      url: '/audit/v1/auditEvent',
      headers,
      params: {
        'resource.type': params?.resources,
        q: params.search?.trim() || undefined, // Full-text search. Trimmed so a blank box sends nothing at all.
        from: params.from ? new Date(params.from).toISOString() : undefined, // RFC 3339 format, e.g. 2025-01-01T00:00:00Z)
        to: params.to ? new Date(params.to).toISOString() : undefined // RFC 3339 format, e.g. 2025-01-01T00:00:00Z)
      },
      paramsSerializer: p => qs.stringify(p, { arrayFormat: 'comma' }),
      originalResponse: true
    })

    return {
      data: response.data || [],
      nextCursor: response.headers?.['x-pagination-cursor-next'] || null
    }
  }

  /**
   * Downloads one page of the organization audit trail as a single file.
   *
   * The export route serves both kinds of event, so this one pins `eventKind` to `AUDIT`.
   * Every filter is named exactly as `list` names it, so an export holds what the table
   * is showing.
   *
   * @param {string} organizationId - Organization identifier sent via the `Organization-Id` header.
   * @param {Object} params
   * @param {string}  [params.format='JSON'] - File format: `JSON`, `CSV`, `XML` or `XLSX`. Case-insensitive.
   * @param {Array}   [params.resources]     - Filter by resource types (serialized as comma-separated values).
   * @param {string}  [params.search]        - Free-text query, sent as `q`.
   * @param {string|Date} [params.from]      - Start of the time range (converted to RFC 3339).
   * @param {string|Date} [params.to]        - End of the time range (converted to RFC 3339).
   * @returns {Promise<{blob: Blob, filename: string, total: number|null, hasMore: boolean, kinds: Array}>}
   */
  async download (organizationId, params = {}) {
    let response = null
    try {
      response = await this.request({
        ...exportRequestOptions,
        headers: {
          'Organization-Id': organizationId
        },
        params: {
          ...exportBaseParams(params),
          eventKind: 'AUDIT',
          'resource.type': params?.resources
        },
        paramsSerializer: p => qs.stringify(p, { arrayFormat: 'comma' })
      })
    } catch (err) {
      throw await readBlobError(err)
    }

    return exportResult(response, params.format, 'AUDIT')
  }
}

export default AuditTrails

export {
  AuditTrails
}
